import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Edit, Trash2, Shield, ShieldCheck, ShieldAlert,
  Mail, Calendar, Check, X, Eye, EyeOff, Key
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { getAuthHeaders, user: currentUser, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'operateur',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`, getAuthHeaders());
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.full_name || !formData.password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      await axios.post(`${API}/users`, formData, getAuthHeaders());
      toast.success('Utilisateur créé avec succès');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      const updateData = {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        is_active: formData.is_active
      };
      
      // Only include password if it was changed
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await axios.put(`${API}/users/${selectedUser.id}`, updateData, getAuthHeaders());
      toast.success('Utilisateur modifié avec succès');
      setShowEditModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeaders());
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      password: '',
      role: user.role,
      is_active: user.is_active !== false
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role: 'operateur',
      is_active: true
    });
    setSelectedUser(null);
    setShowPassword(false);
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      admin: { color: 'bg-red-100 text-red-700 border-red-200', icon: ShieldAlert, label: 'Administrateur' },
      operateur: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Shield, label: 'Opérateur' },
      commercial: { color: 'bg-green-100 text-green-700 border-green-200', icon: ShieldCheck, label: 'Commercial' },
      comptable: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: ShieldCheck, label: 'Comptable' }
    };
    const style = roleStyles[role] || roleStyles.operateur;
    const Icon = style.icon;
    
    return (
      <Badge className={`${style.color} border`}>
        <Icon className="w-3 h-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const getRoleDescription = (roleId) => {
    const descriptions = {
      admin: 'Accès complet à toutes les fonctionnalités',
      operateur: 'Gestion des ventes, clients, produits et production',
      commercial: 'Gestion des ventes et clients uniquement',
      comptable: 'Accès aux finances, ventes et analyses'
    };
    return descriptions[roleId] || '';
  };

  if (!hasPermission('can_manage_users')) {
    return (
      <div className="p-8 text-center">
        <ShieldAlert className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-700 mb-2">Accès Refusé</h2>
        <p className="text-slate-500">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Users className="w-8 h-8 text-[#1B3268]" />
            Gestion des Utilisateurs
          </h1>
          <p className="text-slate-500 mt-1">Gérer les comptes et les droits d'accès</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-[#1B3268] hover:bg-[#152a55] text-white"
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Nouvel Utilisateur
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Utilisateurs</p>
              <p className="text-3xl font-bold">{users.length}</p>
            </div>
            <Users className="w-10 h-10 opacity-80" />
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-500 to-red-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Administrateurs</p>
              <p className="text-3xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
            </div>
            <ShieldAlert className="w-10 h-10 opacity-80" />
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Actifs</p>
              <p className="text-3xl font-bold">{users.filter(u => u.is_active !== false).length}</p>
            </div>
            <Check className="w-10 h-10 opacity-80" />
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Inactifs</p>
              <p className="text-3xl font-bold">{users.filter(u => u.is_active === false).length}</p>
            </div>
            <X className="w-10 h-10 opacity-80" />
          </div>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-4 text-left font-semibold text-slate-700">Utilisateur</th>
                <th className="p-4 text-left font-semibold text-slate-700">Email</th>
                <th className="p-4 text-center font-semibold text-slate-700">Rôle</th>
                <th className="p-4 text-center font-semibold text-slate-700">Statut</th>
                <th className="p-4 text-center font-semibold text-slate-700">Créé le</th>
                <th className="p-4 text-center font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    Chargement...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${user.email}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1B3268] flex items-center justify-center text-white font-bold">
                          {user.full_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{user.full_name}</p>
                          {user.id === currentUser?.id && (
                            <span className="text-xs text-blue-600">(Vous)</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4" />
                        {user.email}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="p-4 text-center">
                      {user.is_active !== false ? (
                        <Badge className="bg-green-100 text-green-700">Actif</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Inactif</Badge>
                      )}
                    </td>
                    <td className="p-4 text-center text-slate-600">
                      <div className="flex items-center justify-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(user)}
                          className="text-blue-600"
                          data-testid={`edit-user-${user.email}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600"
                            data-testid={`delete-user-${user.email}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Roles Reference Card */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#1B3268]" />
          Référence des Rôles
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {roles.map((role) => (
            <div key={role.id} className="p-4 bg-slate-50 rounded-lg border">
              <div className="flex items-center gap-2 mb-2">
                {getRoleBadge(role.id)}
              </div>
              <p className="text-sm text-slate-600 mb-3">{getRoleDescription(role.id)}</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(role.permissions).map(([perm, value]) => (
                  value && (
                    <span key={perm} className="text-xs bg-white px-2 py-1 rounded border text-slate-600">
                      {perm.replace('can_', '').replace(/_/g, ' ')}
                    </span>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#1B3268]" />
              Nouvel Utilisateur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom complet *</Label>
              <Input
                placeholder="Jean Dupont"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                data-testid="user-fullname-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="jean@agrabat.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  data-testid="user-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="operateur">Opérateur</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="comptable">Comptable</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{getRoleDescription(formData.role)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleCreateUser} className="bg-[#1B3268] hover:bg-[#152a55]" data-testid="confirm-create-user">
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#1B3268]" />
              Modifier l'utilisateur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom complet</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Nouveau mot de passe (laisser vide pour ne pas changer)</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData({...formData, role: v})}
                disabled={selectedUser?.id === currentUser?.id}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="operateur">Opérateur</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="comptable">Comptable</SelectItem>
                </SelectContent>
              </Select>
              {selectedUser?.id === currentUser?.id && (
                <p className="text-xs text-amber-600">Vous ne pouvez pas modifier votre propre rôle</p>
              )}
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label>Compte actif</Label>
                <p className="text-xs text-slate-500">Désactiver pour bloquer l'accès</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                disabled={selectedUser?.id === currentUser?.id}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
            <Button onClick={handleUpdateUser} className="bg-[#1B3268] hover:bg-[#152a55]">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
